import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";

import {Calendar, Image, MapPin, Video} from 'lucide-react';

import {useAuth} from "@/hooks/useAuth";
import {Header} from "@/components/Header";
import {toast} from "react-toastify";
import {useAxiosPrivate} from "@/hooks/useAxiosPrivate";
import {fetchAllPastEventsWithMemories} from "@/service/eventApi";
import {LightBoxModal} from "@/components/LightBoxModal";

export const Memories = () => {
	const navigate = useNavigate();

	const {isAuthenticated} = useAuth();

	const axiosPrivate = useAxiosPrivate();

	const [loadingMemoriesPageBaseData, setLoadingMemoriesPageBaseData] = useState(true);
	const [eventsData, setEventsData] = useState(null);

	const [selectedEvent, setSelectedEvent] = useState(null);
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [activeMediaType, setActiveMediaType] = useState(null);
	const [activeMedia, setActiveMedia] = useState(null);

	/**
	 * Converts a UTC ISO string to a formatted, human-readable date
	 * in the user's local timezone and specified language.
	 *
	 * @param {string} isoString - The ISO 8601 date string.
	 * @param {string} [locale='en-US'] - The BCP 47 language tag (e.g., 'en-US', 'mr-IN').
	 * @param {'long' | 'short'} [monthFormat='long'] - 'long' (September) or 'short' (Sep).
	 * @returns {string} The formatted date string.
	 */
	function formatISOTtoLocalDate(isoString, locale = 'en-US', monthFormat = 'long') {
		if (!isoString) {
			return "Invalid Date";
		}

		const date = new Date(isoString);

		if (isNaN(date.getTime())) {
			return "Invalid Date";
		}

		const options = {
			year: 'numeric',
			month: monthFormat,
			day: 'numeric',
		};

		// Use the provided locale
		return new Intl.DateTimeFormat(locale, options).format(date);
	}

	// This function will be passed to the EventCard
	const handleViewPhotos = (event) => {
		console.log("(Memories.jsx | handleViewPhotos) Function Start");
		const images = event.memories.images;

		console.log("(Memories.jsx | handleViewPhotos) Images: ", images.length);
		if (images.length > 0) {
			setSelectedEvent(event);
			setActiveMedia(images);
			setActiveMediaType('image');
			setIsLightboxOpen(true);
		} else {
			toast.info('Photos are currently under processing for this event.');
		}
	};

	// This function will also be passed to the EventCard
	const handleViewVideos = (event) => {
		const videos = event.memories.videos;

		if (videos.length > 0) {
			setSelectedEvent(event);
			setActiveMedia(videos);
			setActiveMediaType('video');
			setIsLightboxOpen(true);
		} else {
			toast.info('Videos are currently under processing for this event.');
		}
	};

	const handleCloseLightbox = () => {
		setIsLightboxOpen(false);
		setSelectedEvent(null);
		setActiveMedia(null);
		setActiveMediaType(null);
	};

	const events = [
		{
			id: 1,
			title: "Summer Music Festival 2024",
			date: "August 15, 2024",
			location: "Central Park Amphitheater",
			bannerUrl: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800&q=80",
			photos: [
				"https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800&q=80",
				"https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800&q=80",
				"https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80"
			],
			videos: ["dQw4w9WgXcQ", "jNQXAC9IVRw"]
		},
		{
			id: 2,
			title: "Tanmay - Community Garden Day",
			date: "September 22, 2024",
			location: "Riverside Community Garden",
			attendees: 150,
			description: "Neighbors came together to plant, learn, and grow our community garden.",
			bannerUrl: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80",
			photos: [
				"https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80",
				"https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&q=80",
				"https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=800&q=80"
			],
			videos: ["me23oH6rJIk"]
		},
		{
			id: 3,
			title: "Fall Food Festival",
			date: "October 10, 2024",
			location: "Downtown Square",
			attendees: 750,
			description: "A celebration of local cuisine with cooking demonstrations, tastings, and family fun.",
			bannerUrl: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80",
			photos: [
				"https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&q=80",
				"https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&q=80",
				"https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80"
			],
			videos: ["kJQP7kiw5Fk"]
		}
	];

	useEffect(() => {
		const getAllPastEventsWithMemories = async () => {
			console.log("(Memories.jsx | getAllPastEventsWithMemories) Function Start");
			try {
				const response = await fetchAllPastEventsWithMemories(axiosPrivate);
				console.log("(Memories.jsx) Events with Memories: ", response);
				if (response.success) {
					response.data.map((event) => {
						event.startDate = formatISOTtoLocalDate(event.startDate);
						event.endDate = formatISOTtoLocalDate(event.endDate);
					})

					console.log("(Memories.jsx) Formatted Events Data with Memories: ", response);
					setEventsData(response.data);
				} else {
					toast.dismiss();
					toast.error(response.message);
				}
			} catch (error) {
				console.log("(Memories.jsx) Error fetching Events Events with Memories: ", error);
				toast.dismiss();
				toast.error(error.message);
			} finally {
				setLoadingMemoriesPageBaseData(false);
			}
		}

		if (!isAuthenticated) {
			navigate("/login", {
				replace: true,
			});
		} else {
			getAllPastEventsWithMemories();
		}
	}, [isAuthenticated]);

	if (loadingMemoriesPageBaseData) {
		return (
			<div className="w-screen min-h-screen bg-white flex flex-col">
				<Header/>
				<div className="flex-1 flex items-center justify-center">
					<div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#D26A53]"></div>
				</div>
			</div>
		);
	}

	return (
		<div
			// className={"w-[100vw] h-[100dvh] bg-white flex flex-col items-center justify-center"}
			className={`w-full bg-white overflow-y-scroll no-scrollbar
			    flex flex-col items-center justify-center`}
		>
			<Header/>

			<img
				src={`/banners/banner-aathvani.jpg`}
				alt={`Memories`}
				className={`w-full portrait:h-[22.5rem] object-cover object-right-bottom shadow-lg`}
			/>

			{/* Events Grid */}
			<main
				className={`w-full relative mx-auto px-4 sm:px-6 lg:px-8 py-12
					flex justify-center items-start`}
			>
				{
					eventsData && eventsData.length > 0
						?
						(
							<div
								className={`lg:max-w-7xl xl:max-w-[88rem] 2xl:max-w-[104rem]
									grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}
							>
								{
									eventsData.map((event, index) => (
										<div
											key={index}
											className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300`}
										>
											{/* Event Banner Image | Event Title */}
											<div className={`relative overflow-hidden`}>
												{/* Event Banner Image */}
												<img
													src={event.bannerUrl}
													alt={event.title}
													className={`h-full object-cover aspect-auto 
														hover:scale-110 transition-transform duration-500`}
												/>

												{/* Background Gradient */}
												<div
													className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent`}
												/>

												{/* Event Title */}
												<div className={`absolute bottom-4 left-4 right-4`}>
													<h3 className={`text-xl font-custom-bold text-white`}>
														{event.title}
													</h3>
												</div>
											</div>

											{/* Event Details */}
											<div className="p-6">
												{/* Event Date & Location */}
												<div className="space-y-3 mb-4">
													{/* Date */}
													<div className="flex items-center text-gray-600">
														<Calendar className="w-4 h-4 mr-2"/>
														<span className="text-sm font-custom-medium">
															{
																event.startDate !== event.endDate
																	? `${event.startDate} - ${event.endDate}`
																	: event.startDate
															}
														</span>
													</div>

													{/* Location */}
													<div className="flex items-center text-gray-600">
														<MapPin className="w-4 h-4 mr-2"/>
														<span className="text-sm font-custom-medium">
												{event.location.name}
											</span>
													</div>
												</div>

												{/* View Photos and Videos Buttons */}
												<div
													className="flex items-center justify-between pt-4 border-t border-gray-100">
													<button
														className="flex items-center"
														// onClick={() => setSelectedEvent(event)}
														onClick={() => handleViewPhotos(event)}
													>
														<Image className="w-4 h-4 mr-1"/>
														<span className="text-sm text-indigo-600 font-custom-medium">
															View Photos
														</span>
													</button>
													<button
														className="flex items-center"
														// onClick={() => setSelectedEvent(event)}
														onClick={() => handleViewVideos(event)}
													>
														<Video className="w-4 h-4 mr-1"/>
														<span className="text-sm text-purple-600 font-custom-medium">
															View Videos
														</span>
													</button>
												</div>
											</div>
										</div>
									))
								}
							</div>
						)
						:
						(
							<div className={`flex-1 flex flex-col justify-center items-center gap-y-2 text-gray-600`}>
								<img
									src={"/assets/memories-filler.png"}
									alt={`More Memories Coming Soon`}
									className={`portrait:h-[15rem] landscape:h-[20rem] aspect-square object-cover`}
								/>

								<h1 className={"text-[1.75rem] text-center font-custom-mono-bold"}>
									Memories are in the making!
								</h1>
							</div>
						)
				}
			</main>

			{/* Modal for detailed view */}
			<LightBoxModal
				eventDetails={{
					areMemoriesAvailable: selectedEvent?.areMemoriesAvailable,
					bannerUrl: selectedEvent?.bannerUrl,
					endDate: selectedEvent?.endDate,
					endTime: selectedEvent?.endTime,
					location: selectedEvent?.location,
					startDate: selectedEvent?.startDate,
					startTime: selectedEvent?.startTime,
					title: selectedEvent?.title,
					_id: selectedEvent?._id
				}}
				isOpen={isLightboxOpen}
				onClose={handleCloseLightbox}
				mediaType={activeMediaType}
				media={activeMedia}
			/>
		</div>
	);
};